/**
 * PostCard — Listendarstellung eines einzelnen Newsfeed-Beitrags.
 */
import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Pin, MoreVertical, CheckCircle2, MessageSquare,
  Building, MapPin, Globe, Tag,
} from 'lucide-react';
import { CATEGORY_ICONS, timeAgo } from './helpers';

const getVisibilityIcon = (type) => {
  if (type === 'branch') return Building;
  if (type === 'location') return MapPin;
  return Globe;
};

const getVisibilityLabel = (type) => {
  if (type === 'branch') return 'Niederlassung';
  if (type === 'location') return 'Standort';
  return 'Alle';
};

export const PostCard = ({
  post, isPinned, currentUserId, canManage, settings, category,
  onOpenDetail, onEdit, onDelete,
}) => {
  const CatIcon = category ? (CATEGORY_ICONS[category.icon] || Tag) : Tag;
  const isAuthor = post.author_id === currentUserId;
  const VisIcon = getVisibilityIcon(post.visibility_type);

  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all ${isPinned ? 'border-l-4 border-l-amber-400' : ''}`}
      onClick={() => onOpenDetail(post)}
      data-testid={`post-card-${post.id}`}
    >
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              {isPinned && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 gap-1">
                  <Pin className="h-3 w-3" /> Angepinnt
                </Badge>
              )}
              {category && (
                <Badge variant="outline" className="text-xs gap-1"
                  style={{ borderColor: category.color, color: category.color }}>
                  <CatIcon className="h-3 w-3" /> {category.name}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <VisIcon className="h-3 w-3" />
                {getVisibilityLabel(post.visibility_type)}
              </span>
            </div>
            <h3 className="font-semibold text-base mb-1 truncate">{post.title}</h3>
            <p className="text-sm text-muted-foreground line-clamp-2">{post.content}</p>
          </div>
          {(isAuthor || canManage) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(post); }}>
                  Bearbeiten
                </DropdownMenuItem>
                <DropdownMenuItem className="text-destructive"
                  onClick={(e) => { e.stopPropagation(); onDelete(post.id); }}>
                  Löschen
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t text-xs text-muted-foreground">
          <span>{post.author_name} &middot; {timeAgo(post.created_at)}</span>
          <div className="flex items-center gap-3">
            {post.require_confirmation && settings.confirmations_enabled && (
              <span className="flex items-center gap-1">
                <CheckCircle2 className={`h-3.5 w-3.5 ${post.user_confirmed ? 'text-green-500' : 'text-gray-400'}`} />
                {post.confirmation_count}
              </span>
            )}
            {settings.comments_enabled && post.allow_comments !== false && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" /> {post.comment_count}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
